// AudioWizard — name + transcript → POST /audio-assets.

import React, { useEffect, useRef, useState } from 'react'
import WizardShell, { inputStyle, labelStyle, tokens } from './WizardShell'
import { CustomAssetManager } from '../../../lib/CustomAssetManager'
import { assetRegistry } from '../../../lib/assetRegistry'

function baseName(file) {
  return (file?.name ?? '').replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_\-]+/g, '_')
}

export default function AudioWizard({ file, path = [], onDone, onCancel }) {
  const [name, setName] = useState(baseName(file))
  const [transcript, setTranscript] = useState('')
  const [durationMs, setDurationMs] = useState(null)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const previewRef = useRef(null)

  useEffect(() => {
    if (!file) return
    const url = URL.createObjectURL(file)
    if (previewRef.current) previewRef.current.src = url
    const probe = new Audio(url)
    probe.addEventListener('loadedmetadata', () => {
      if (isFinite(probe.duration)) setDurationMs(Math.round(probe.duration * 1000))
    })
    return () => URL.revokeObjectURL(url)
  }, [file])

  const submit = async () => {
    if (!name.trim()) { setError('Name is required'); return }
    setSubmitting(true); setError(null)
    try {
      const row = await CustomAssetManager.uploadAudioAsset(file, {
        name: name.trim(),
        transcript: transcript.trim() || undefined,
        durationMs,
      })
      assetRegistry.setPath(`audio:${row.id}`, path)
      onDone?.(row)
    } catch (e) {
      setError(String(e.message ?? e))
      setSubmitting(false)
    }
  }

  return (
    <WizardShell
      title="New audio asset"
      subtitle={file?.name}
      onCancel={onCancel}
      onSubmit={submit}
      submitDisabled={submitting || !name.trim()}
      submitLabel={submitting ? 'Uploading…' : 'Upload'}
      error={error}
    >
      <label style={labelStyle}>Name</label>
      <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} autoFocus />

      <label style={labelStyle}>Transcript (optional)</label>
      <textarea
        value={transcript}
        onChange={e => setTranscript(e.target.value)}
        rows={3}
        style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
      />

      <label style={labelStyle}>Preview</label>
      <audio ref={previewRef} controls style={{ width: '100%' }} />

      <div style={{ marginTop: 8, fontFamily: 'var(--m-mono)', fontSize: 10, color: tokens.dim }}>
        Duration: {durationMs != null ? `${(durationMs/1000).toFixed(2)}s` : '—'}
      </div>
    </WizardShell>
  )
}
